import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Download,
  Inbox,
  Loader2,
  Search,
  Sparkles,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { DisambiguaDialog } from "@/components/m3/DisambiguaDialog";
import { PacchettoDrawer } from "@/components/m3/PacchettoDrawer";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useM3DocumentiPacchetti,
  useM3DocumentiUpload,
  useM3EliminaPacchetto,
} from "@/hooks/useM3";
import { apiDownloadFile } from "@/lib/api";
import { cn } from "@/lib/utils";
import type {
  DocumentiUploadResponse,
  FiltroPacchetti,
  PaccoBase,
  Sorgenti,
  StatoPacchetto,
} from "@/types/api";

// ── Helpers di presentazione ──────────────────────────────────────────

const STATO_PACCHETTO: Record<StatoPacchetto, { label: string; cls: string }> = {
  bozza: { label: "Bozza", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  da_completare: {
    label: "Da completare",
    cls: "bg-amber-50 text-amber-700 border-amber-200",
  },
  completo: { label: "Completo", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

function formatData(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
}

function SorgentiBadges({ sorgenti }: { sorgenti: Sorgenti | null }) {
  if (!sorgenti) return null;
  const items: Array<[string, boolean, string]> = [
    ["NDP", sorgenti.ndp.stato === "agganciata", sorgenti.ndp.cosa_manca ?? "NDP agganciata"],
    ["DDT", sorgenti.ddt.stato === "agganciata", sorgenti.ddt.cosa_manca ?? "DDT agganciato"],
    ["FT", sorgenti.fattura.stato === "agganciata", sorgenti.fattura.cosa_manca ?? "Fattura agganciata"],
  ];
  return (
    <div className="flex gap-1">
      {items.map(([label, ok, title]) => (
        <span
          key={label}
          title={title}
          className={cn(
            "flex h-[22px] w-[26px] items-center justify-center rounded border text-[9px] font-bold",
            ok
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-dashed border-border bg-muted text-muted-foreground",
          )}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function ProgressPacchetto({ pronti, totale }: { pronti: number; totale: number }) {
  const pct = totale ? Math.round((pronti / totale) * 100) : 0;
  return (
    <div className="flex min-w-[130px] items-center gap-2">
      <span className="h-1.5 flex-1 overflow-hidden rounded bg-slate-100">
        <span className="block h-full rounded bg-brand-700" style={{ width: `${pct}%` }} />
      </span>
      <span className="whitespace-nowrap text-[11px] font-semibold text-muted-foreground">
        {pronti} / {totale}
      </span>
    </div>
  );
}

// ── Dropzone full-width ────────────────────────────────────────────────

// Fasi reali della pipeline dell'agente (classifica → estrai → match → azione),
// usate come didascalie mentre l'upload è in corso.
const FASI_UPLOAD = [
  "Leggo il documento…",
  "Riconosco il tipo (NDP / DDT / fattura)…",
  "Estraggo i dati…",
  "Aggancio al pacchetto…",
  "Genero i documenti…",
];

function DocumentiDropzone({
  onOpenPacchetto,
  onOpenDisambigua,
}: {
  onOpenPacchetto: (id: string) => void;
  onOpenDisambigua: (res: DocumentiUploadResponse) => void;
}) {
  const upload = useM3DocumentiUpload();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [esito, setEsito] = useState<DocumentiUploadResponse | null>(null);
  const [progress, setProgress] = useState(0);
  const [fase, setFase] = useState(0);

  // Avanzamento «morbido»: non avendo eventi reali dal server, la barra sale in
  // modo asintotico verso ~92% e le didascalie scorrono sulle fasi; al termine
  // (success/errore) `isPending` torna false e tutto si azzera.
  useEffect(() => {
    if (!upload.isPending) {
      setProgress(0);
      setFase(0);
      return;
    }
    setProgress(8);
    setFase(0);
    const avanza = setInterval(() => {
      setProgress((p) => Math.min(p + Math.max(1.2, (92 - p) * 0.06), 92));
    }, 400);
    const fasi = setInterval(
      () => setFase((i) => Math.min(i + 1, FASI_UPLOAD.length - 1)),
      2800,
    );
    return () => {
      clearInterval(avanza);
      clearInterval(fasi);
    };
  }, [upload.isPending]);

  function invia(file: File) {
    setEsito(null);
    upload.mutate(
      { file },
      {
        onSuccess: (res) => {
          if (res.pacco) onOpenPacchetto(res.pacco.carico_id);
          else if (res.esito === "in_coda_match") onOpenDisambigua(res);
          else setEsito(res);
        },
      },
    );
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) invia(file);
          e.target.value = "";
        }}
      />
      <div
        role="button"
        tabIndex={0}
        onClick={() => !upload.isPending && inputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) invia(file);
        }}
        className={cn(
          "flex cursor-pointer items-center gap-4 rounded-xl border-[1.5px] border-dashed p-4 transition-colors",
          dragOver
            ? "border-brand-600 bg-brand-50"
            : "border-border bg-gradient-to-b from-brand-50/40 to-card hover:border-brand-400 hover:bg-brand-50",
        )}
      >
        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-brand-700 text-white">
          {upload.isPending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Upload className="h-5 w-5" />
          )}
        </div>
        {upload.isPending ? (
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between text-sm font-bold">
              <span>{FASI_UPLOAD[fase]}</span>
              <span className="tabular-nums text-xs font-semibold text-muted-foreground">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded bg-slate-100">
              <div
                className="h-full rounded bg-brand-700 transition-[width] duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">
              Aida sta elaborando il documento — pochi secondi.
            </div>
          </div>
        ) : (
          <>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold">Trascina qui NDP, DDT o fattura</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                La NDP crea un nuovo pacchetto · DDT e fattura si agganciano al pacchetto
                giusto (se ambiguo, ti chiedo a quale)
              </div>
            </div>
            <div className="hidden flex-none gap-1.5 sm:flex">
              {["NDP", "DDT", "Fattura"].map((t) => (
                <span
                  key={t}
                  className="rounded-full border border-border bg-card px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground"
                >
                  {t}
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {esito && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-3 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-800"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
            <div className="flex-1">{esito.messaggio}</div>
            <button onClick={() => setEsito(null)} className="text-current/60 hover:text-current">
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Il testo cercato, ma solo quando si smette di scrivere.
 *
 * Qui la ricerca è **server-side** (è il server a conoscere numero NDP e DDT, che
 * stanno nelle estrazioni, non nella riga), e quella risposta ricalcola lo stato
 * di tutti i pacchetti: senza il ritardo, «FIDRIK» sono sei chiamate da qualche
 * secondo l'una, e l'ultima a rispondere non è detta sia l'ultima partita.
 */
function useRicercaRitardata(testo: string, ritardo = 350): string {
  const [valore, setValore] = useState(testo);
  useEffect(() => {
    const t = setTimeout(() => setValore(testo.trim()), ritardo);
    return () => clearTimeout(t);
  }, [testo, ritardo]);
  return valore;
}

// ── Vista ────────────────────────────────────────────────────────────────

const FILTRI: Array<{ id: FiltroPacchetti; label: string }> = [
  { id: "aperti", label: "Aperti" },
  { id: "completi", label: "Completi" },
  { id: "tutti", label: "Tutti" },
];

export function DocumentiView() {
  const [filtro, setFiltro] = useState<FiltroPacchetti>("aperti");
  const [ricerca, setRicerca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [selId, setSelId] = useState<string | null>(null);
  const [disambigua, setDisambigua] = useState<DocumentiUploadResponse | null>(null);
  const [daEliminare, setDaEliminare] = useState<PaccoBase | null>(null);

  // Mentre si cerca il filtro passa a «tutti»: un pacchetto completo cercato
  // dalla vista «Aperti» non deve risultare inesistente. È la stessa regola del
  // tabellone — un meccanismo alla volta, nessun «non lo trovo» perché era
  // filtrato altrove.
  const cercando = ricerca.trim().length > 0;
  const q = useRicercaRitardata(ricerca);
  const { data, isLoading, isFetching } = useM3DocumentiPacchetti(
    cercando ? "tutti" : filtro,
    q || undefined,
    pagina,
  );
  const items = data?.items ?? [];
  const totale = data?.totale ?? 0;
  const perPagina = data?.per_pagina ?? 25;
  const ultima = Math.max(1, Math.ceil(totale / perPagina));
  const paginaCorrente = data?.pagina ?? pagina;

  return (
    <div className="space-y-4">
      <div className="max-w-3xl">
        <h2 className="text-lg font-bold tracking-tight">Documenti</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Trascina la NDP per creare il pacchetto documenti; DDT e fatture si agganciano da
          sole e completano i campi mancanti. I pacchetti nascono anche dalle NDP che Aida
          riceve via email.
        </p>
      </div>

      <DocumentiDropzone onOpenPacchetto={setSelId} onOpenDisambigua={setDisambigua} />

      {!!data?.pacchetti_da_aida && (
        <div className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5" />
          Aida ha creato {data.pacchetti_da_aida}{" "}
          {data.pacchetti_da_aida === 1 ? "pacchetto" : "pacchetti"} dalle email di oggi.
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <div className="flex overflow-hidden rounded-md border border-border">
          {FILTRI.map((f) => (
            <button
              key={f.id}
              onClick={() => {
                setFiltro(f.id);
                setPagina(1);
              }}
              className={cn(
                "px-3 py-1.5 text-[12px] font-semibold",
                filtro === f.id
                  ? "bg-brand-50 text-brand-700"
                  : "bg-card text-muted-foreground hover:bg-muted",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative min-w-[15rem] flex-1 sm:max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={ricerca}
            onChange={(e) => {
              setRicerca(e.target.value);
              setPagina(1);
            }}
            placeholder="Cerca cliente, paese, NDP o DDT…"
            className="h-[34px] w-full rounded-md border bg-card pl-8 pr-8 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {cercando && (
            <button
              type="button"
              onClick={() => {
                setRicerca("");
                setPagina(1);
              }}
              aria-label="Pulisci la ricerca"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <span className="ml-auto shrink-0 text-[12px] text-muted-foreground">
          {cercando
            ? `${totale} ${totale === 1 ? "risultato" : "risultati"} per «${ricerca.trim()}»`
            : `${data?.conteggio?.aperti ?? 0} aperti · ${data?.conteggio?.completi_mese ?? 0} completi questo mese`}
        </span>
      </div>

      {/* Tabella */}
      <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
        {isLoading ? (
          <div className="space-y-2 p-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
            <Inbox className="h-7 w-7" />
            <div className="text-sm font-medium">
              {cercando
                ? `Nessun pacchetto per «${ricerca.trim()}».`
                : "Nessun pacchetto in questa vista."}
            </div>
            <div className="text-xs">
              {cercando
                ? "La ricerca guarda cliente, paese, numero NDP e numero DDT."
                : "Trascina una NDP per generarne uno."}
            </div>
          </div>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-border bg-brand-50/40 text-left text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2.5">Rif.</th>
                <th className="px-3 py-2.5">Cliente</th>
                <th className="px-3 py-2.5">Data</th>
                <th className="px-3 py-2.5">Sorgenti</th>
                <th className="px-3 py-2.5">Documenti</th>
                <th className="px-3 py-2.5">Stato</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <PacchettoRow
                  key={p.carico_id}
                  pacco={p}
                  selected={p.carico_id === selId}
                  onClick={() => setSelId(p.carico_id)}
                  onElimina={() => setDaEliminare(p)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginazione: compare solo quando c'è più di una pagina — con dieci
          pacchetti sarebbe una barra che non fa niente. */}
      {ultima > 1 && (
        <div className="flex items-center justify-between gap-3 text-[12px] text-muted-foreground">
          <span>
            {(paginaCorrente - 1) * perPagina + 1}–
            {Math.min(paginaCorrente * perPagina, totale)} di {totale}
            {isFetching && " · aggiorno…"}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={paginaCorrente <= 1}
              onClick={() => setPagina((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-1 font-medium hover:bg-muted disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Indietro
            </button>
            <span className="px-1.5 tabular-nums">
              {paginaCorrente} / {ultima}
            </span>
            <button
              type="button"
              disabled={paginaCorrente >= ultima}
              onClick={() => setPagina((p) => p + 1)}
              className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-1 font-medium hover:bg-muted disabled:opacity-40"
            >
              Avanti <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {selId && <PacchettoDrawer caricoId={selId} onClose={() => setSelId(null)} />}
      {disambigua && (
        <DisambiguaDialog
          upload={disambigua}
          onClose={() => setDisambigua(null)}
          onAssegnato={(cid) => {
            setDisambigua(null);
            setSelId(cid);
          }}
        />
      )}
      {daEliminare && (
        <EliminaPacchettoDialog
          pacco={daEliminare}
          onClose={() => setDaEliminare(null)}
          onEliminato={() => {
            if (selId === daEliminare.carico_id) setSelId(null);
            setDaEliminare(null);
          }}
        />
      )}
    </div>
  );
}

function EliminaPacchettoDialog({
  pacco,
  onClose,
  onEliminato,
}: {
  pacco: PaccoBase;
  onClose: () => void;
  onEliminato: () => void;
}) {
  const elimina = useM3EliminaPacchetto();
  const [confermato, setConfermato] = useState(false);

  return (
    <Dialog open onOpenChange={(o) => !o && !elimina.isPending && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="h-5 w-5" /> Elimina pacchetto
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>
            Stai per eliminare il pacchetto{" "}
            <span className="font-semibold text-foreground">
              {pacco.numero_ndp ? `NDP ${pacco.numero_ndp} — ` : ""}
              {pacco.cliente_ragione_sociale}
            </span>{" "}
            con tutti i suoi documenti e dati. L'operazione è{" "}
            <span className="font-semibold text-red-700">irreversibile</span>.
          </p>
          <label className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2.5 text-[12.5px] text-red-800">
            <input
              type="checkbox"
              checked={confermato}
              onChange={(e) => setConfermato(e.target.checked)}
              className="mt-0.5"
            />
            Confermo di voler eliminare definitivamente questo pacchetto.
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={elimina.isPending}>
              Annulla
            </Button>
            <Button
              variant="destructive"
              disabled={!confermato || elimina.isPending}
              onClick={() => elimina.mutate(pacco.carico_id, { onSuccess: onEliminato })}
            >
              {elimina.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Elimina definitivamente
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PacchettoRow({
  pacco,
  selected,
  onClick,
  onElimina,
}: {
  pacco: PaccoBase;
  selected: boolean;
  onClick: () => void;
  onElimina: () => void;
}) {
  const stato = STATO_PACCHETTO[pacco.stato_pacchetto];
  return (
    <tr
      onClick={onClick}
      className={cn(
        "cursor-pointer border-b border-border text-[12.5px] hover:bg-brand-50/40",
        selected && "bg-brand-50 shadow-[inset_3px_0_0] shadow-brand-700",
      )}
    >
      <td className="whitespace-nowrap px-3 py-3 font-semibold tabular-nums">
        {pacco.numero_ndp ? `NDP ${pacco.numero_ndp}` : "—"}
      </td>
      <td className="px-3 py-3">
        <div className="font-semibold">{pacco.cliente_ragione_sociale}</div>
        <div className="text-[11px] text-muted-foreground">
          {pacco.paese_iso ?? "—"}
          {pacco.paese_nome ? ` · ${pacco.paese_nome}` : ""}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-3 tabular-nums">{formatData(pacco.data)}</td>
      <td className="px-3 py-3">
        <SorgentiBadges sorgenti={pacco.sorgenti} />
      </td>
      <td className="px-3 py-3">
        {pacco.progresso && (
          <ProgressPacchetto pronti={pacco.progresso.pronti} totale={pacco.progresso.totale} />
        )}
      </td>
      <td className="px-3 py-3">
        <span
          className={cn(
            "inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold",
            stato.cls,
          )}
        >
          {pacco.genesi === "email_aida" && pacco.stato_pacchetto === "bozza"
            ? "Bozza · da Aida"
            : stato.label}
        </span>
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center justify-end gap-1.5">
          {/* Senza data di spedizione il pacchetto non si genera: si deve
              vedere dalla lista, non aprendo i pacchetti uno per uno. */}
          {pacco.manca_data_documenti && (
            <span
              title="Manca la data di spedizione: finché non c'è, i documenti non vengono generati"
              className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
            >
              <CalendarDays className="h-3 w-3" /> manca la data
            </span>
          )}
          {pacco.template_mancanti > 0 && (
            <span
              title={`${pacco.template_mancanti} documenti senza template caricato (Impostazioni → Template)`}
              className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700"
            >
              <AlertTriangle className="h-3 w-3" /> {pacco.template_mancanti} senza template
            </span>
          )}
          {pacco.template_update && (
            <span
              title="Template aggiornato disponibile"
              className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700"
            >
              tpl{pacco.template_versione_disponibile ? ` v${pacco.template_versione_disponibile}` : ""} ▲
            </span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              apiDownloadFile(`/api/v1/m3/documenti/pacchetti/${pacco.carico_id}/zip`);
            }}
            title="Scarica zip"
            className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-brand-700"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onElimina();
            }}
            title="Elimina pacchetto"
            className="rounded-md border border-border p-1.5 text-muted-foreground hover:border-red-200 hover:text-red-600"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}
